import { useState } from "react";
import { createTranslator } from "next-intl";
import { Alert, CircularProgress, Link } from "@mui/material";
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useStyles, shipmentStatuses } from "./utils";
import { getOrder } from "../../api/order";
import { MainLayout } from "../../layouts/MainLayout";
import { Card } from "../../shared/Card";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import { Loader } from "../../shared/Loader";
import content from "../../content/tracking.json";

export function Tracking() {
  const { siTheme, locale } = useTenantTheme();
  const styles = useStyles();
  const { shipInsureId } = useParams();
  const [alertMessage, setAlertMessage] = useState("");
  const [shipments, setShipments] = useState([]);
  const [alert, setAlert] = useState();
  const [order, setOrder] = useState();
  const [loading, setLoading] = useState(false);
  const [activeShipmentIndex, setActiveShipmentIndex] = useState(0);

  const t = createTranslator({
    messages: content[locale],
  });

  useEffect(() => {
    const lookupOrder = async () => {
      setLoading(true);

      try {
        const response = await getOrder(shipInsureId, {
          include_carrier_detail: 1,
        });

        if (response.data?.line_items) {
          setShipments(response.data?.shipments || []);
          setOrder(response.data);
          setAlertMessage("");
        }
      } catch (error) {
        let message = error.message;

        if (error.response?.status === 404) {
          message = error.response.data.Message;
        }

        setAlert("error");
        setAlertMessage(message);
      } finally {
        setLoading(false);
      }
    };

    lookupOrder();
  }, []);

  const getShipmentStatus = () => {
    return (
      shipmentStatuses[order?.shipments.at(activeShipmentIndex)?.status] || ""
    );
  };

  const getShipmentStep = (status) => {
    switch (status) {
      case "delivered":
      case "return_to_sender": {
        return 3;
      }
      case "in_transit":
      case "available_for_pickup":
      case "out_for_delivery": {
        return 2;
      }
      case "waiting_for_pickup":
      case "failure":
      case "cancelled":
      case "unknown_carrier":
      case "alert":
      case "error":
      default: {
        return 1;
      }
    }
  };

  const renderFileClaimButton = () => {
    if (order?.fee > 0) {
      return (
        <Box mt="46px">
          <Button href="/claims" variant="contained" fullWidth>
            {t("fileClaim")}
          </Button>
        </Box>
      );
    }
  };

  const onSelectShipment = (index) => {
    setActiveShipmentIndex(index);
  };

  const renderOrderInformation = () => {
    if (order === undefined) {
      return;
    }

    const Item = ({ children }) => (
      <Typography
        paragraph
        fontSize="13px"
        lineHeight="20px"
        marginBottom="5px"
      >
        {children}
      </Typography>
    );

    return (
      <Box marginBottom="30px">
        <Item>
          ShipInsureID: <b>{shipInsureId}</b>{" "}
          <Typography
            component="span"
            color={order?.fee > 0 ? "#09B825" : "#FF3E33"}
            fontWeight="bold"
            fontSize="13px"
          >
            {order.fee > 0 ? t("covered") : t("notCovered")}
          </Typography>
        </Item>
        <Item>
          {t("store")}: <b>{order.store_name}</b>
        </Item>
        <Item>
          {t("orderNumber")}: <b>{order.order_number}</b>
        </Item>
        {order?.shipments.length > 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            gap="18px"
            paddingTop="12px"
            paddingBottom="12px"
          >
            {order?.shipments.map((shipment, index) => (
              <Button
                key={shipment.id}
                variant={
                  activeShipmentIndex === index ? "contained" : "outlined"
                }
                sx={{ pl: 4, pr: 4 }}
                onClick={() => onSelectShipment(index)}
              >
                {shipment.tracking_number}
              </Button>
            ))}
          </Box>
        )}
        <Item>
          {t("carrier")}:{" "}
          <b>{order?.shipments.at(activeShipmentIndex)?.carrier || "n/a"}</b>
        </Item>
        <Item>
          {t("trackingNumber")}:{" "}
          <Link
            sx={{ textDecoration: "none" }}
            fontWeight="bold"
            href={order?.shipments.at(activeShipmentIndex)?.tracking_url}
            target="_blank"
          >
            {order?.shipments.at(activeShipmentIndex)?.tracking_number}
          </Link>
        </Item>
      </Box>
    );
  };

  const renderShipmentSummary = () => {
    if (order === undefined) {
      return;
    }

    return (
      <Box mb="30px">
        <Typography
          paragraph
          fontSize="16px"
          lineHeight="30px"
          fontWeight={700}
        >
          {t("status")}: {getShipmentStatus()}
        </Typography>

        <Stepper
          activeStep={getShipmentStep(
            order?.shipments.at(activeShipmentIndex)?.status,
          )}
          alternativeLabel
          className={styles.statusStepper}
        >
          <Step>
            <StepLabel>{t("pending")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("inTransit")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("delivered")}</StepLabel>
          </Step>
        </Stepper>
      </Box>
    );
  };

  const renderShipmentEvents = () => {
    const steps = shipments.at(activeShipmentIndex)?.carrier_details?.details;

    if (steps === undefined) {
      return;
    }
    const activeStep = steps.length;

    return (
      <div>
        <Typography
          mb="10px"
          paragraph
          fontSize="16px"
          lineHeight="30px"
          fontWeight={700}
        >
          {t("history")}:
        </Typography>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          className={styles.historyStepper}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>
                {step.message}: {step.city}, {step.state} {step.zipcode}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
    );
  };

  const { isThemeLoading } = useTenantTheme();

  // each feature has its own loader that requires a theme (Claims/Tracking);
  // it could be argued that we fetch tracking data the same way we do claims
  // by getting rid of the card loader on this particular page
  if (isThemeLoading) {
    return <Loader />;
  }
  return (
    <MainLayout helpText={t("helpText")}>
      <Card
        enableBrand={!siTheme.isPrimaryTheme}
        poweredByText={t("poweredByText")}
      >
        {alertMessage && (
          <Box mb="35px">
            <Alert
              onClose={() => setAlertMessage("")}
              variant="filled"
              severity={alert}
              color={alert}
            >
              {alertMessage}
            </Alert>
          </Box>
        )}
        <Typography
          variant="h3"
          fontSize="28px"
          fontWeight={700}
          fontFamily="Montserrat, serif"
          marginBottom="10px"
        >
          {t("title")}
        </Typography>

        {loading && (
          <Box
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            py="75px"
          >
            <CircularProgress />
          </Box>
        )}
        {!loading && renderOrderInformation()}
        {!loading && renderShipmentSummary()}
        {!loading && renderShipmentEvents()}
        {!loading && renderFileClaimButton()}
      </Card>
    </MainLayout>
  );
}
